/**
 * Agents v3 — the agency's custom-skill catalog on the Skills tab
 * (`/agents/skills`).
 *
 * Lets an agency upload its own **Agent Skills** — a `.zip` (a folder with a
 * `SKILL.md`) that becomes the agents' extra know-how (a house reporting style,
 * a checklist, a glossary). Uploads go to Anthropic's Skills API via central-api
 * and land in the org's catalog. A new skill is used by EVERY scheduled agent
 * type by default; the "Used by" toggles per row detach/attach it per type
 * (agency-wide — `PUT …/custom-skills/{id}/assignments`). Deleting an agency
 * skill archives it (AdUp "house" skills are read-only here).
 *
 * These are the Anthropic Agent Skills — the agents' uploaded know-how — NOT the
 * Claude-Code org skills library; the copy says so to keep the two distinct.
 *
 * Manager-only (`portal.settings.manage`, passed as `canManage`); reviewers see
 * nothing. Self-loading. Styles are the shared `client-agents__*` set from
 * `../../client-reports/client-reports.scss`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import Popup from '../../../ui/popup/Popup';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  listCustomSkills,
  uploadCustomSkill,
  deleteCustomSkill,
  setCustomSkillAssignments,
  type CustomSkill,
  type TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import type { Translate } from '../../client-reports/agents/agent-helpers';
import '../../client-reports/client-reports.scss';

interface CustomSkillsManagerProps {
  slug: string;
  /** `portal.settings.manage`; false → the card renders nothing. */
  canManage: boolean;
  /** Bumped after every upload/delete/assignment change (hosts that cache the catalog). */
  onCatalogChanged?: () => void;
  /**
   * The scheduled agent types for the per-row "Used by" toggles (registry
   * `available_types`, chat types filtered out). Omitted → no toggles.
   */
  agentTypes?: TypeView[];
}

function friendlyUploadError(err: any, t: Translate): string {
  if (err?.status === 422) {
    const v = err.validation || {};
    const first =
      v.file?.[0] || v.name?.[0] || v.description?.[0];
    if (first) return first;
    return (
      err.message ||
      t(
        'AgentSkillsRejected',
        'The skill package was rejected — it must be a .zip containing a SKILL.md file.'
      )
    );
  }
  return (
    err?.message ||
    t('AgentSkillsUploadFailed', 'Failed to upload the skill — try again.')
  );
}

const CustomSkillsManager = ({
  slug,
  canManage,
  onCatalogChanged,
  agentTypes = [],
}: CustomSkillsManagerProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [skills, setSkills] = useState<CustomSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /** The agency skill queued for a delete confirm (null = no dialog). */
  const [pendingDelete, setPendingDelete] = useState<CustomSkill | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** `${skillId}:${agentType}` of the toggle whose assignment PUT is in flight. */
  const [assigning, setAssigning] = useState<string | null>(null);

  const toast = useCallback(
    (message: string, messageType: 'success' | 'error' | 'info') =>
      dispatch(setMessage({ message, messageType })),
    [dispatch]
  );

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listCustomSkills(slug);
      setSkills(Array.isArray(rows) ? rows : []);
    } catch (err: any) {
      setLoadError(
        err?.message ||
          t('AgentSkillsLoadFailed', 'Failed to load your custom skills.')
      );
    } finally {
      setLoading(false);
    }
    // `t` is left out: a language switch must not re-fetch the catalog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (canManage) load();
  }, [load, canManage]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSubmit = name.trim().length > 0 && !!file && !uploading;

  const doUpload = async () => {
    if (!canSubmit || !file) return;
    setUploadError(null);
    setUploading(true);
    try {
      await uploadCustomSkill(slug, {
        name: name.trim(),
        description: description.trim() || undefined,
        file,
      });
      resetForm();
      await load();
      onCatalogChanged?.();
      toast(
        t(
          'AgentSkillsUploaded',
          'Skill uploaded — every agent uses it from its next run.'
        ),
        'success'
      );
    } catch (err: any) {
      setUploadError(friendlyUploadError(err, t));
      toast(friendlyUploadError(err, t), 'error');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteCustomSkill(slug, pendingDelete.id);
      setPendingDelete(null);
      await load();
      onCatalogChanged?.();
      toast(t('AgentSkillsRemoved', 'Skill removed.'), 'success');
    } catch (err: any) {
      toast(
        err?.message ||
          t('AgentSkillsRemoveFailed', 'Failed to remove the skill.'),
        'error'
      );
    } finally {
      setDeleting(false);
    }
  };

  /** Attach/detach a skill for one agent type (agency-wide). */
  const setUsedBy = async (skill: CustomSkill, agentType: string, on: boolean) => {
    if (assigning) return;
    const current = Array.isArray(skill.agent_types) ? skill.agent_types : [];
    const next = on
      ? Array.from(new Set([...current, agentType]))
      : current.filter((id) => id !== agentType);
    setAssigning(`${skill.id}:${agentType}`);
    try {
      const result = await setCustomSkillAssignments(slug, skill.id, next);
      setSkills((prev) =>
        prev.map((s) =>
          s.id === skill.id ? { ...s, agent_types: result.data?.agent_types ?? next } : s
        )
      );
      onCatalogChanged?.();
      const label =
        agentTypes.find((type) => type.agent_type === agentType)?.label ||
        agentType;
      toast(
        on
          ? t('AgentSkillsNowUses', '{{agent}} now uses “{{skill}}”.', {
              agent: label,
              skill: skill.name,
            })
          : t(
              'AgentSkillsNoLongerUses',
              '{{agent}} no longer uses “{{skill}}”.',
              { agent: label, skill: skill.name }
            ),
        'success'
      );
    } catch (err: any) {
      toast(
        err?.message ||
          t(
            'AgentSkillsAssignFailed',
            'Failed to change where this skill is used.'
          ),
        'error'
      );
    } finally {
      setAssigning(null);
    }
  };

  if (!canManage) return null;

  return (
    <>
      <section className='client-agents__card client-agents__settings'>
        <div className='client-agents__card-head'>
          <div className='client-agents__card-id'>
            <h3 className='client-agents__card-title'>
              {t('AgentSkillsTitle', 'Custom skills')}
            </h3>
          </div>
        </div>
        <p className='client-agents__card-hint'>
          {t(
            'AgentSkillsHintBefore',
            'Upload your own skills to give the agents extra know-how — a house reporting style, a checklist, a glossary. A skill is a folder with a'
          )}{' '}
          <code>SKILL.md</code>
          {t(
            'AgentSkillsHintAfter',
            ", zipped. A new skill is used by every agent; switch it off per agent below if it shouldn't be. These are the agents' Agent Skills, separate from the Claude Code skills library."
          )}
        </p>

        {/* ---------------- Upload ---------------- */}
        <div className='client-agents__settings-section'>
          <h4 className='client-agents__settings-title'>
            {t('AgentSkillsUploadTitle', 'Upload a skill')}
          </h4>
          <label className='client-agents__field-label' htmlFor='custom-skill-name'>
            {t('AgentSkillsName', 'Name')}
          </label>
          <input
            id='custom-skill-name'
            className='client-agents__input'
            type='text'
            value={name}
            maxLength={120}
            disabled={uploading}
            placeholder={t(
              'AgentSkillsNamePlaceholder',
              'e.g. ACME house reporting style'
            )}
            onChange={(e) => setName(e.target.value)}
          />
          <label
            className='client-agents__field-label'
            htmlFor='custom-skill-desc'
          >
            {t('AgentSkillsDescription', 'Description (optional)')}
          </label>
          <input
            id='custom-skill-desc'
            className='client-agents__input'
            type='text'
            value={description}
            maxLength={1024}
            disabled={uploading}
            placeholder={t(
              'AgentSkillsDescriptionPlaceholder',
              'What this skill helps the agent do'
            )}
            onChange={(e) => setDescription(e.target.value)}
          />
          <label className='client-agents__field-label' htmlFor='custom-skill-file'>
            {t('AgentSkillsPackage', 'Skill package (.zip containing SKILL.md)')}
          </label>
          <input
            id='custom-skill-file'
            ref={fileInputRef}
            className='client-agents__file'
            type='file'
            accept='.zip,application/zip'
            disabled={uploading}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {uploadError && (
            <p className='client-agents__validation' role='alert'>
              {uploadError}
            </p>
          )}
          <div className='client-agents__settings-buttons'>
            <MainSquareButton
              type='button'
              value={
                uploading
                  ? t('AgentTemplateUploading', 'Uploading…')
                  : t('AgentSkillsUploadButton', 'Upload skill')
              }
              buttonType='success'
              normal
              loading={uploading}
              disabled={!canSubmit}
              message={
                !canSubmit
                  ? t('AgentSkillsAddNameAndZip', 'Add a name and a .zip file.')
                  : ''
              }
              onClick={doUpload}
            />
          </div>
        </div>

        {/* ---------------- Catalog ---------------- */}
        <div className='client-agents__settings-section'>
          <h4 className='client-agents__settings-title'>
            {t('AgentSkillsYours', 'Your skills')}
          </h4>
          {loading ? (
            <p className='client-agents__muted'>
              {t('AgentSkillsLoading', 'Loading your skills…')}
            </p>
          ) : loadError ? (
            <div>
              <p className='client-agents__validation'>{loadError}</p>
              <div style={{ marginTop: '1rem' }}>
                <MainSquareButton
                  type='button'
                  value={t('Retry', 'Retry')}
                  buttonType='light'
                  normal
                  onClick={load}
                />
              </div>
            </div>
          ) : skills.length === 0 ? (
            <p className='client-agents__muted'>
              {t(
                'AgentSkillsNone',
                'No custom skills yet. Upload one above and every agent starts using it from its next run.'
              )}
            </p>
          ) : (
            <div className='client-agents__skills'>
              {skills.map((s) => (
                <div className='client-agents__skill-row' key={s.id}>
                  <div className='client-agents__skill-text'>
                    <span className='client-agents__skill-label'>
                      {s.name}
                      {s.source === 'adup' && (
                        <span className='client-agents__skill-default'>
                          {' '}
                          · AdUp
                        </span>
                      )}
                    </span>
                    {s.description && (
                      <span className='client-agents__skill-desc'>
                        {s.description}
                      </span>
                    )}
                  </div>
                  <div className='client-agents__skill-actions'>
                    {agentTypes.length > 0 && (
                      <div
                        className='client-agents__usedby'
                        aria-label={t('AgentSkillsUsedByNamed', 'Used by — {{skill}}', {
                          skill: s.name,
                        })}
                      >
                        <span className='client-agents__usedby-label'>
                          {t('AgentSkillsUsedBy', 'Used by')}
                        </span>
                        {agentTypes.map((type) => {
                          const on = Array.isArray(s.agent_types)
                            ? s.agent_types.includes(type.agent_type)
                            : false;
                          return (
                            <label
                              key={type.agent_type}
                              className='client-agents__usedby-item'
                            >
                              <ToggleSwitch
                                id={`skill-usedby-${s.id}-${type.agent_type}`}
                                isOn={on}
                                disabled={assigning !== null}
                                onClick={(checked: boolean) =>
                                  setUsedBy(s, type.agent_type, checked)
                                }
                              />
                              <span>{type.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {s.source === 'adup' ? (
                      <span className='client-agents__skill-state'>
                        {t('AgentSkillsReadOnly', 'Read-only')}
                      </span>
                    ) : (
                      <MainSquareButton
                        type='button'
                        value={t('Remove', 'Remove')}
                        buttonType='light'
                        normal
                        onClick={() => setPendingDelete(s)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---- delete confirmation ---- */}
      {pendingDelete && (
        <Popup
          trigger={!!pendingDelete}
          setTrigger={() => {
            if (!deleting) setPendingDelete(null);
          }}
        >
          <div className='client-agents__confirm'>
            <h3 className='client-agents__confirm-title'>
              {t('AgentSkillsRemoveConfirmTitle', 'Remove “{{skill}}”?', {
                skill: pendingDelete.name,
              })}
            </h3>
            <p className='client-agents__confirm-body'>
              {t(
                'AgentSkillsRemoveConfirmBody',
                "Every agent that uses this skill simply drops it on its next run — nothing else changes. This can't be undone; re-upload to add it back."
              )}
            </p>
            <div className='client-agents__confirm-actions'>
              <MainSquareButton
                type='button'
                value={t('Cancel', 'Cancel')}
                buttonType='light'
                normal
                disabled={deleting}
                onClick={() => setPendingDelete(null)}
              />
              <MainSquareButton
                type='button'
                value={
                  deleting
                    ? t('AgentTemplateRemoving', 'Removing…')
                    : t('AgentSkillsYesRemove', 'Yes — remove it')
                }
                buttonType='danger'
                normal
                loading={deleting}
                disabled={deleting}
                onClick={doDelete}
              />
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};

export default CustomSkillsManager;
